import { useMemo } from 'react';
import { formatCodeName } from '../common/utils';
import {
  ActivePeriod,
  DataObjectGroupType,
  DataObjectOutputType,
  Frequency,
  GroupDescriptionRef,
} from '../dataobject';
import {
  type DataObjectGroupsWithDescriptionsMapById,
  useDataObjectGroupsWithDescriptionsQuery,
} from '../dataObjectGroup';

export interface GroupDescriptionOption {
  key: string;
  label: string;
  value: number | GroupDescriptionRef;
  groupType: DataObjectGroupType;
  outputType?: DataObjectOutputType;
  code: string;
  frequency: Frequency;
  activePeriods: ActivePeriod[];
  descriptionId?: number;
  hasComments?: boolean;
  hasAccess?: boolean;
}

export type GroupDescriptionOptions = [
  GroupDescriptionOption[],
  GroupDescriptionOption[],
  GroupDescriptionOption[],
];

export function useGroupDescriptionOptions(
  groupsById?: DataObjectGroupsWithDescriptionsMapById,
  excludeIntermediates = false,
  valueMapping: 'ref' | 'groupId' = 'ref',
) {
  return useMemo<GroupDescriptionOptions>(() => {
    const descriptionGroupOptions: GroupDescriptionOption[] = [];
    const ungroupedDescriptionOptions: GroupDescriptionOption[] = [];
    const descriptionOptions: GroupDescriptionOption[] = [];

    for (const [id, group] of groupsById ?? []) {
      if (id === null) {
        continue;
      }

      if (group.isSystemic) {
        const [description] = group.descriptions;

        if (
          excludeIntermediates &&
          description.outputTypeCode === 'INTERMEDIATE'
        ) {
          continue;
        }

        ungroupedDescriptionOptions.push({
          key: `description-${description.id}`,
          value:
            valueMapping === 'groupId'
              ? description.dataObjectGroupId
              : `${description.dataObjectGroupId}-${description.id}`,
          label: formatCodeName(description),
          groupType: description.groupType,
          outputType: description.outputTypeCode as DataObjectOutputType,
          code: description.code,
          frequency: description.dataObjectGroupData?.frequency
            ?.code as Frequency,
          activePeriods: description.activePeriods ?? [],
          descriptionId: description.id,
          hasComments: description.hasComments,
          hasAccess: description.hasAccess,
        });
      } else {
        const groupType = group.groupType;
        const frequency = group.dataObjectGroupData?.frequency
          ?.code as Frequency;

        descriptionGroupOptions.push({
          key: `group-${group.id}`,
          value: valueMapping === 'groupId' ? group.id : `${group.id}`,
          label: formatCodeName(group),
          groupType,
          // output type is defined for a group only if all descriptions have the same output type
          outputType:
            group.descriptions.every(
              (d) => d.outputTypeCode === group.descriptions[0].outputTypeCode,
            ) && group.descriptions.length > 0
              ? (group.descriptions[0].outputTypeCode as DataObjectOutputType)
              : undefined,
          code: group.code,
          frequency,
          activePeriods: group.descriptions.flatMap(
            (d) => d.activePeriods ?? [],
          ),
        });
        group.descriptions
          ?.filter(
            (description) =>
              !excludeIntermediates ||
              description.outputTypeCode !== 'INTERMEDIATE',
          )
          ?.forEach((description) =>
            descriptionOptions.push({
              key: `description-${description.id}`,
              value:
                valueMapping === 'groupId'
                  ? group.id
                  : `${group.id}-${description.id}`,
              label: formatCodeName(description),
              groupType,
              outputType: description.outputTypeCode as DataObjectOutputType,
              code: description.code,
              frequency,
              activePeriods: description.activePeriods ?? [],
              descriptionId: description.id,
              hasComments: description.hasComments,
              hasAccess: description.hasAccess,
            }),
          );
      }
    }

    descriptionGroupOptions.sort((a, b) =>
      a.label.localeCompare(b.label, 'et', { sensitivity: 'accent' }),
    );
    ungroupedDescriptionOptions.sort((a, b) =>
      a.label.localeCompare(b.label, 'et', { sensitivity: 'accent' }),
    );
    descriptionOptions.sort((a, b) =>
      a.label.localeCompare(b.label, 'et', { sensitivity: 'accent' }),
    );

    return [
      descriptionGroupOptions,
      ungroupedDescriptionOptions,
      descriptionOptions,
    ];
  }, [excludeIntermediates, groupsById, valueMapping]);
}

export function useGroupOrDescriptionOptions(groupType?: DataObjectGroupType) {
  const { data } = useDataObjectGroupsWithDescriptionsQuery({
    groupType,
  });

  return useGroupDescriptionOptions(data);
}

export const getGroupDescriptionFrequency = (
  ref: string,
  options: GroupDescriptionOptions,
) =>
  options.flat().find((option) => option.value.toString() === ref)?.frequency;
